import { Alert, Button } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { ApplicationTypes } from 'metalworks-domain'
import { useCallback, useEffect } from 'react'

import { useLogger } from '@salsita-ai/platform-project-sdk/react'
import { Dialog, PhxIcon } from '@salsita-ai/platform-project-sdk/ui'

import { MetalworksAnalytics, analyticsService } from '../../../systems/analytics'
import { configuratorInterface } from '../../../systems/configurator-interface'
import { FinalizationSubmitButton } from '../FinalizationSubmitButton'
import { FORM_ID, JobDetailsForm } from '../shared/JobDetailsForm'
import { FinalizationState, useFinalize } from '../useFinalize'

interface SinglePlateJobDetailsDialogProps {
  onSuccess: (returnUrl: string | null) => void
}

export const SinglePlateJobDetailsDialog = (props: SinglePlateJobDetailsDialogProps) => {
  const { onSuccess } = props

  const { navigationSystem, storeSystem } = configuratorInterface.useDomain()
  const logger = useLogger()

  const { finalizationState, finalize, reset, returnUrl } = useFinalize()

  const handleBack = useCallback(() => {
    analyticsService.push(MetalworksAnalytics.BackToSummaryButtonClicked())
    storeSystem.stageAndCommit([navigationSystem.getNavigationUpdate(ApplicationTypes.NavigationState.Summary)])
    reset()
  }, [navigationSystem, reset, storeSystem])

  useEffect(() => {
    if (finalizationState === FinalizationState.Done) {
      onSuccess(returnUrl)
    }
  }, [returnUrl, finalizationState, onSuccess])

  const { mutate, isError } = useMutation({
    mutationKey: ['finalize'],
    mutationFn: finalize,
    onError: error => {
      logger.error(`Finalization failed`, {}, error)
    },
  })

  return (
    <Dialog.Root variant="static" sx={{ width: '90%', maxWidth: '1000px' }}>
      <Dialog.ContentLayout
        controlsPosition="end"
        body={
          <>
            <JobDetailsForm onSubmit={mutate} formId={FORM_ID} />
            {isError && (
              <Alert severity="error">
                Configuration details could not be sent. Please try again later. If the issue persists, contact us on
                1300&nbsp;652&nbsp;692.
              </Alert>
            )}
          </>
        }
        controls={
          <>
            <Button
              variant="text"
              onClick={handleBack}
              startIcon={<PhxIcon name="ArrowLeft" />}
              disabled={finalizationState !== FinalizationState.NotStarted}
            >
              Back to Summary
            </Button>

            <FinalizationSubmitButton form={FORM_ID} type="submit" state={finalizationState}>
              Create Quote
            </FinalizationSubmitButton>
          </>
        }
      />
    </Dialog.Root>
  )
}
